import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    OnInit,
    Signal,
    signal,
    viewChild,
    WritableSignal
} from "@angular/core";
import {AuthService, LoginType, provideEntityModel, ValidationMessage} from "@fedelta-pos/data-client";
import {Page} from "../../navigation/page";
import {LoadingService} from "../../ui-elements/loading/loading.service";
import {NotificationService} from "../../notification/notification.service";
import {IdentityModel} from '../identity.model';
import {DateFieldComponent} from '../../inputs/date-field/date-field.component';
import {EmailFieldComponent} from '../../inputs/email-field/email-field.component';
import {FieldPairComponent} from '../../inputs/form/field-pair/field-pair.component';
import {FormComponent} from '../../inputs/form/form.component';
import {PhoneNumberFieldComponent} from '../../inputs/phone-number-field/phone-number-field.component';
import {OptionComponent} from '../../inputs/selection-field/option.component';
import {SelectionFieldComponent} from '../../inputs/selection-field/selection-field.component';
import {TextAreaFieldComponent} from '../../inputs/text-area-field/text-area-field.component';
import {TextFieldComponent} from '../../inputs/text-field/text-field.component';
import {formatDateForEdit} from '../../utils/date-time-utils';
import {IdentityEntityModel} from "../identity-entity.model";
import {
    CustomerDetailsPanelComponent
} from '../../customer-info/customer-details-panel/customer-details-panel.component';
import {CustomerDetails, CustomerDetailsService} from '../../customer-info/customer-details.service';
import {OrderingContextModel} from '../../ordering-context/ordering-context.model';
import {TranslateModule} from '@ngx-translate/core';
import {ModalPageLayoutComponent} from '../../page-layout/modal-page-layout/modal-page-layout.component';
import {MemberCardPanelComponent} from "../member-card-panel/member-card-panel.component";
import {FormsModule} from '@angular/forms';
import {EmailDirective} from '../../inputs/email-field/email.directive';
import {PhoneNumberDirective} from '../../inputs/phone-number-field/phone-number.directive';
import {ButtonComponent} from '../../buttons/button/button.component';
import {LinkComponent} from '../../buttons/link/link.component';
import {PageFooterControlDirective} from '../../page-layout/page-footer-control.directive';

@Component({
    selector: "woa-profile-edit-page",
    templateUrl: "profile-edit-page.component.html",
    styleUrl: "./profile-edit-page.component.scss",
    providers: [
        provideEntityModel(IdentityEntityModel),
        {provide: Page, useExisting: ProfileEditPageComponent}
    ],
    imports: [
        ButtonComponent,
        CustomerDetailsPanelComponent,
        DateFieldComponent,
        EmailDirective,
        EmailFieldComponent,
        FieldPairComponent,
        FormComponent,
        FormsModule,
        LinkComponent,
        MemberCardPanelComponent,
        ModalPageLayoutComponent,
        OptionComponent,
        PageFooterControlDirective,
        PhoneNumberDirective,
        PhoneNumberFieldComponent,
        SelectionFieldComponent,
        TextAreaFieldComponent,
        TextFieldComponent,
        TranslateModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class ProfileEditPageComponent extends Page implements OnInit {
    private notificationService: NotificationService = inject(NotificationService);
    private loadingService: LoadingService = inject(LoadingService);
    public identityEntityModel: IdentityEntityModel = inject(IdentityEntityModel);
    public identityModel: IdentityModel = inject(IdentityModel);
    private orderingContextModel: OrderingContextModel = inject(OrderingContextModel);
    public authService: AuthService = inject(AuthService);
    private customerDetailsService: CustomerDetailsService = inject(CustomerDetailsService);

    LoginType = LoginType;

    public readonly form: Signal<FormComponent | undefined> = viewChild(FormComponent);

    public editingMember: WritableSignal<boolean> = signal<boolean>(false);
    public editingCustomer: WritableSignal<boolean> = signal<boolean>(false);
    public customerDetails: WritableSignal<CustomerDetails> = signal<CustomerDetails>(this.customerDetailsService.loadCustomer());

    public readonly canEditMember: Signal<boolean> = computed<boolean>(() =>
        this.identityModel.currentMember() && this.identityModel.currentMember().allowProfileEdit);

    async ngOnInit(): Promise<void> {
        await this.identityEntityModel.loadEntity(this.authService.getLoggedInIdentityId());
        if (this.identityEntityModel.dataSignal()?.dob) {
            this.identityEntityModel.data.dob = formatDateForEdit(new Date(this.identityEntityModel.dataSignal()?.dob.substring(0, 10)));
        }
    }

    get memberEmail(): string {
        return this.identityEntityModel.dataSignal()?.email;
    }

    set memberEmail(email: string) {
        this.identityEntityModel.data.email = email?.toLowerCase();
    }

    get customerEmail(): string {
        return this.customerDetails().email;
    }

    set customerEmail(email: string) {
        this.customerDetails().email = email?.toLowerCase();
    }

    forgetCustomer(): void {
        this.customerDetailsService.removeCustomer();
        this.orderingContextModel.applyIdentityContactDetails(this.identityModel.currentIdentity(), this.orderingContextModel.applySavedCustomerDetails(true));
        void this.dismiss();
    }

    saveProfile(): void {
        if (!this.form()?.valid) {
            return;
        }

        if (this.editingMember()) {
            void this.loadingService.performWithSpinner(async () => {
                try {
                    const validationMessages: ValidationMessage[] = await this.identityEntityModel.saveEntity();
                    if (validationMessages) {
                        this.form()?.applyServerValidationMessages(validationMessages);
                    } else {
                        this.identityModel.applyIdentity(this.identityEntityModel.dataSignal());
                        void this.dismiss();
                    }
                } catch (error) {
                    return this.notificationService.displayFooterError(error, "woa.notification.profileChangeFailed");
                }
            });
        } else {
            this.customerDetailsService.saveCustomer(this.customerDetails());
            this.orderingContextModel.applyIdentityContactDetails(this.identityModel.currentIdentity(), this.orderingContextModel.applySavedCustomerDetails(true));
            void this.dismiss();
        }
    }
}
